(() => {
  'use strict';
  if (window.STHSavedItems) return;
  const KEY = 'sth_wishlist';
  const LIST_KEY = 'sth_wishlist_lists';
  const DEFAULT_LIST = 'saved-for-later';
  const ALL_LISTS = 'all';
  const PAGE_SIZE = 12;
  const CARD_SELECTOR = '.product-card, .sth-discovery-card';
  const HEART = '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.7"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z"/></svg>';
  let rootPath = '/', currency = 'USD', locale = 'en', page, visible = PAGE_SIZE;
  let generation = 0, noticeTimer, scanTimer, storageUnavailable = false;
  let activeList = ALL_LISTS, definitions = [], listsUnavailable = false, emptyCopy;
  const usedListIds = new Set();
  const cache = new Map();
  const pending = new Map();
  const requestVersions = new Map();
  let incarnations = new Map(), lastAdded = 0;
  const queue = [];
  let active = 0;

  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const handleValid = value => typeof value === 'string' && /^[a-z0-9][a-z0-9-]*$/i.test(value);
  const productURL = handle => new URL(rootPath + 'products/' + encodeURIComponent(handle), location.origin).href;
  function imageURL(value) {
    if (typeof value !== 'string' || !value.trim()) return '';
    try { const url = new URL(value, location.origin); return url.protocol === 'https:' ? url.href : ''; } catch (_) { return ''; }
  }
  function handleFromURL(value) {
    try {
      const url = new URL(value, location.origin);
      if (url.origin !== location.origin || url.protocol !== 'https:') return '';
      const match = url.pathname.match(/\/products\/([^/]+)\/?$/);
      const handle = match ? decodeURIComponent(match[1]) : '';
      return handleValid(handle) ? handle : '';
    } catch (_) { return ''; }
  }
  const validId = value => (typeof value === 'string' || Number.isSafeInteger(value)) && /^[1-9]\d*$/.test(String(value));
  function priceObservation(value) {
    if (!value || value.version !== 1 || !validId(value.productId) || typeof value.currency !== 'string' || !/^[A-Z]{3}$/.test(value.currency)) return null;
    if (!Number.isSafeInteger(value.min) || !Number.isSafeInteger(value.max) || value.min < 0 || value.max < value.min) return null;
    if (!Number.isSafeInteger(value.observedAt) || value.observedAt <= 0 || value.observedAt > Date.now()) return null;
    if (!Array.isArray(value.variantIds) || !value.variantIds.length || value.variantIds.length >= 250 || !value.variantIds.every(validId)) return null;
    const variantIds = value.variantIds.map(String).sort();
    if (new Set(variantIds).size !== variantIds.length) return null;
    return { version: 1, productId: String(value.productId), currency: value.currency, min: value.min, max: value.max, variantIds, observedAt: value.observedAt };
  }
  function comparablePrices(first, next) {
    return first && next && first.productId === next.productId && first.currency === next.currency && first.variantIds.length === next.variantIds.length && first.variantIds.every((id, index) => id === next.variantIds[index]);
  }
  const savedNote = value => typeof value === 'string' ? value.slice(0, 400) : '';
  const savedPriority = value => value === 'high' || value === 'low' ? value : 'normal';
  const customListId = value => typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
  const storedListId = value => customListId(value) ? value.toLowerCase() : DEFAULT_LIST;
  const listName = value => typeof value === 'string' ? value.normalize('NFKC').trim().replace(/\s+/g, ' ') : '';
  const listNameKey = value => listName(value).toLowerCase().replace(/\u00df/g, 'ss').replace(/\u03c2/g, '\u03c3');
  const reservedListName = value => ['all saved', 'saved for later', ALL_LISTS, DEFAULT_LIST].includes(listNameKey(value));
  function readListState() {
    let raw;
    try { raw = JSON.parse(localStorage.getItem(LIST_KEY) || '[]'); listsUnavailable = false; }
    catch (_) { listsUnavailable = true; return { raw: null, lists: [] }; }
    if (!Array.isArray(raw)) { listsUnavailable = true; return { raw: null, lists: [] }; }
    const unique = new Map();
    raw.forEach((value, index) => {
      if (!value || !customListId(value.id)) return;
      const id = value.id.toLowerCase(), name = listName(value.name);
      usedListIds.add(id);
      if (!name || name.length > 60 || reservedListName(name) || !Number.isSafeInteger(value.created) || value.created < 0) return;
      unique.set(id, { id, name, created: value.created, index });
    });
    const names = new Set();
    const lists = [...unique.values()].filter(value => {
      const key = listNameKey(value.name);
      if (names.has(key)) return false;
      names.add(key); return true;
    });
    return { raw, lists };
  }
  function effectiveListId(item, lists = definitions) {
    return lists.some(list => list.id === item.listId) ? item.listId : DEFAULT_LIST;
  }
  function listLabel(id, lists = definitions) {
    return id === ALL_LISTS ? 'All saved' : lists.find(list => list.id === id)?.name || 'Saved for later';
  }
  function updateListURL() {
    if (!page) return;
    const url = new URL(location.href);
    if (activeList === ALL_LISTS) url.searchParams.delete('list'); else url.searchParams.set('list', activeList);
    if (url.href !== location.href) window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  }
  function loadLists() {
    const state = readListState();
    if (state.raw) {
      definitions = state.lists;
      if (activeList !== ALL_LISTS && activeList !== DEFAULT_LIST && !definitions.some(list => list.id === activeList)) { activeList = ALL_LISTS; visible = PAGE_SIZE; }
    }
    updateListURL(); return state;
  }
  function filteredItems(items) {
    return items.map((item, index) => ({ ...item, index })).filter(item => activeList === ALL_LISTS || effectiveListId(item) === activeList).sort((a, b) => b.added - a.added || b.index - a.index);
  }
  function listOptions(select, lists, selected, items) {
    if (!select) return;
    const choices = [{ id: DEFAULT_LIST, name: 'Saved for later' }, ...lists];
    if (items) choices.unshift({ id: ALL_LISTS, name: 'All saved' });
    select.replaceChildren(...choices.map(list => {
      const count = items ? (list.id === ALL_LISTS ? items.length : items.filter(item => effectiveListId(item, lists) === list.id).length) : null;
      const option = element('option', '', list.name + (count === null ? '' : ' (' + count + ')'));
      option.value = list.id; return option;
    }));
    select.value = selected;
  }
  function renderListTools(items) {
    const controls = page.querySelector('[data-sth-lists]');
    if (!controls) return;
    controls.hidden = false;
    listOptions(controls.querySelector('[data-sth-list-select]'), definitions, activeList, items);
    const custom = definitions.some(list => list.id === activeList);
    controls.querySelectorAll('[data-sth-list-rename],[data-sth-list-delete]').forEach(button => { button.hidden = !custom; });
  }
  function chooseList(id) {
    activeList = id; visible = PAGE_SIZE; renderPage(getItems());
  }
  function newListId(state) {
    const items = getItems();
    if (storageUnavailable) throw new Error('Saved items could not be read. Please try again.');
    items.forEach(item => usedListIds.add(item.listId));
    state.lists.forEach(list => usedListIds.add(list.id));
    for (let attempt = 0; attempt < 8; attempt++) {
      const bytes = new Uint8Array(16);
      let id;
      if (window.crypto?.randomUUID) id = window.crypto.randomUUID();
      else {
        if (!window.crypto?.getRandomValues) throw new Error('A new list could not be created in this browser.');
        window.crypto.getRandomValues(bytes); bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
        const hex = [...bytes].map(value => value.toString(16).padStart(2, '0')).join('');
        id = hex.slice(0, 8) + '-' + hex.slice(8, 12) + '-' + hex.slice(12, 16) + '-' + hex.slice(16, 20) + '-' + hex.slice(20);
      }
      id = id.toLowerCase();
      if (customListId(id) && !usedListIds.has(id)) { usedListIds.add(id); return id; }
    }
    throw new Error('A new list could not be created. Please try again.');
  }
  function cartSelection(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Saved cart options could not be read.');
    if (typeof value.id !== 'string' || !/^[a-z0-9_-]{1,128}$/i.test(value.id)) throw new Error('Saved cart selection is invalid.');
    if (!Number.isSafeInteger(value.variantId) || value.variantId <= 0 || !Number.isSafeInteger(value.quantity) || value.quantity <= 0) throw new Error('The variant or quantity is invalid.');
    if (typeof value.variantTitle !== 'string' || value.variantTitle.length > 300 || typeof value.sourceKey !== 'string' || !value.sourceKey || value.sourceKey.length > 500 || typeof value.pendingRemoval !== 'boolean') throw new Error('Saved cart options are invalid.');
    const properties = value.properties;
    if (!properties || typeof properties !== 'object' || Array.isArray(properties)) throw new Error('Product customizations are invalid.');
    const entries = Object.entries(properties);
    if (entries.length > 100 || entries.some(([key, entry]) => !key || key.length > 200 || typeof entry !== 'string' || entry.length > 2000) || entries.reduce((total, [key, entry]) => total + key.length + (typeof entry === 'string' ? entry.length : 0), 0) > 16000) throw new Error('Product customizations cannot be saved without losing information.');
    return { id: value.id, variantId: value.variantId, quantity: value.quantity, variantTitle: value.variantTitle, properties: Object.fromEntries(entries.sort(([a], [b]) => a.localeCompare(b))), sourceKey: value.sourceKey, pendingRemoval: value.pendingRemoval };
  }
  function cartSelections(raw, strict = false) {
    if (raw.cartSelections === undefined) return [];
    if (!Array.isArray(raw.cartSelections)) { if (strict) throw new Error('Saved cart options could not be read.'); return []; }
    const ids = new Set(), selections = [];
    for (const value of raw.cartSelections) {
      try {
        const selection = cartSelection(value);
        if (ids.has(selection.id)) throw new Error('Saved cart selections conflict.');
        ids.add(selection.id); selections.push(selection);
      } catch (error) { if (strict) throw error; }
    }
    return selections;
  }
  const cartFingerprint = value => JSON.stringify(value, (_, entry) => entry && typeof entry === 'object' && !Array.isArray(entry) ? Object.fromEntries(Object.entries(entry).sort(([a], [b]) => a.localeCompare(b))) : entry);
  const editableCartBaseline = item => ({ note: savedNote(item.note), priority: savedPriority(item.priority), listId: storedListId(item.listId) });
  function readCartRecords() {
    let raw;
    try { raw = JSON.parse(localStorage.getItem(KEY) || '[]'); }
    catch (_) { storageUnavailable = true; throw new Error('Saved items could not be read in this browser.'); }
    if (!Array.isArray(raw)) throw new Error('Saved items could not be read safely.');
    storageUnavailable = false;
    return raw;
  }
  function writeCartRecords(raw) {
    try {
      const serialized = JSON.stringify(raw);
      localStorage.setItem(KEY, serialized);
      if (localStorage.getItem(KEY) !== serialized) throw new Error('Readback did not match.');
      storageUnavailable = false;
    } catch (_) { storageUnavailable = true; throw new Error('Saved items could not be verified. Check your saved items before trying again.'); }
  }
  function cartRecordIndex(raw, handle) {
    for (let index = raw.length - 1; index >= 0; index--) if (record(raw[index])?.handle === handle) return index;
    return -1;
  }
  function freezeCartTicket(value) {
    Object.values(value).forEach(entry => { if (entry && typeof entry === 'object') freezeCartTicket(entry); });
    return Object.freeze(value);
  }
  function cartSelectionId(raw) {
    const ids = new Set(raw.flatMap(item => Array.isArray(item?.cartSelections) ? item.cartSelections.map(selection => selection?.id) : []));
    for (let attempt = 0; attempt < 8; attempt++) {
      let id;
      if (window.crypto?.randomUUID) id = window.crypto.randomUUID();
      else if (window.crypto?.getRandomValues) id = [...window.crypto.getRandomValues(new Uint8Array(24))].map(value => value.toString(16).padStart(2, '0')).join('');
      if (id && !ids.has(id)) return id;
    }
    throw new Error('A saved cart selection could not be created.');
  }
  function ensureCartSelection(product, value) {
    const item = record(product);
    if (!item) throw new Error('This product cannot be saved.');
    const raw = readCartRecords();
    let index = cartRecordIndex(raw, item.handle);
    const selection = cartSelection({ ...value, id: cartSelectionId(raw), pendingRemoval: true });
    if (index >= 0) {
      const selections = cartSelections(raw[index], true);
      const pending = selections.filter(entry => entry.pendingRemoval && entry.sourceKey === selection.sourceKey);
      if (pending.length) {
        if (pending.length !== 1 || pending[0].variantId !== selection.variantId || pending[0].quantity !== selection.quantity || cartFingerprint(pending[0].properties) !== cartFingerprint(selection.properties)) throw new Error('This cart line changed while it was being saved. Check your cart before trying again.');
        const current = record(raw[index]);
        return freezeCartTicket({ handle: current.handle, added: current.added, selectionId: pending[0].id, selection: pending[0], item: current });
      }
      raw[index] = { ...raw[index], cartSelections: [...(raw[index].cartSelections || []), selection] };
    } else {
      const latest = raw.reduce((max, entry) => Number.isSafeInteger(entry?.added) && entry.added < Number.MAX_SAFE_INTEGER ? Math.max(max, entry.added) : max, lastAdded);
      lastAdded = Math.max(Date.now(), latest + 1);
      if (!Number.isSafeInteger(lastAdded)) throw new Error('A new saved item could not be created.');
      const created = { ...item, added: lastAdded, listId: DEFAULT_LIST, cartSelections: [selection] };
      delete created.priceObservation;
      raw.push(created); index = raw.length - 1;
    }
    writeCartRecords(raw);
    const current = record(raw[index]);
    refresh();
    return freezeCartTicket({ handle: current.handle, added: current.added, selectionId: selection.id, selection, item: current });
  }
  function finishCartSelectionRemoval(handle, id, expectedAdded) {
    const raw = readCartRecords(), index = cartRecordIndex(raw, handle);
    if (index < 0) return { status: 'missing', handle };
    const current = record(raw[index]);
    if (expectedAdded !== undefined && current.added !== expectedAdded) return { status: 'replaced', handle };
    const selections = cartSelections(raw[index], true), position = selections.findIndex(selection => selection.id === id);
    if (position < 0) return { status: 'missing', handle };
    if (selections[position].pendingRemoval) {
      const next = raw[index].cartSelections.slice();
      next[position] = { ...next[position], pendingRemoval: false };
      raw[index] = { ...raw[index], cartSelections: next };
      writeCartRecords(raw);
    }
    refresh();
    return { status: 'updated', handle, selectionId: id };
  }
  function beginCartMove(handle, selectionId = null) {
    const raw = readCartRecords(), index = cartRecordIndex(raw, handle);
    if (index < 0) throw new Error('This saved item is no longer available.');
    const item = record(raw[index]), selections = cartSelections(raw[index], true);
    const selection = selectionId === null ? null : selections.find(value => value.id === selectionId);
    if (selectionId !== null && !selection) throw new Error('These saved cart options are no longer available.');
    if (selection?.pendingRemoval) throw new Error('Check the original cart line before moving this saved item.');
    const baseline = editableCartBaseline(item);
    if (selection) baseline.selectionFingerprint = cartFingerprint(raw[index].cartSelections.find(value => value.id === selectionId));
    return freezeCartTicket({ handle, added: item.added, selectionId, selection, baseline });
  }
  function finishCartMove(ticket) {
    if (!ticket || !handleValid(ticket.handle) || !Number.isFinite(ticket.added) || !ticket.baseline || (ticket.selectionId !== null && typeof ticket.selectionId !== 'string')) throw new Error('The saved-item move could not be verified.');
    const raw = readCartRecords(), index = cartRecordIndex(raw, ticket.handle);
    const result = status => ({ status, handle: ticket.handle, removed: false, selectionRemoved: false });
    if (index < 0) return result('missing');
    const current = record(raw[index]);
    if (current.added !== ticket.added) return result('replaced');
    const selections = cartSelections(raw[index], true);
    let selectionRemoved = false;
    if (ticket.selectionId !== null) {
      const position = selections.findIndex(selection => selection.id === ticket.selectionId);
      if (position < 0) return result('missing');
      if (cartFingerprint(selections[position]) !== cartFingerprint(ticket.selection) || cartFingerprint(raw[index].cartSelections[position]) !== ticket.baseline.selectionFingerprint) return result('changed');
      raw[index] = { ...raw[index], cartSelections: raw[index].cartSelections.filter(selection => selection.id !== ticket.selectionId) };
      selectionRemoved = true;
    }
    const baseline = { note: ticket.baseline.note, priority: ticket.baseline.priority, listId: ticket.baseline.listId };
    const unchanged = cartFingerprint(editableCartBaseline(current)) === cartFingerprint(baseline);
    const remaining = cartSelections(raw[index], true);
    const removed = !remaining.length && unchanged;
    if (removed) {
      const kept = raw.filter(value => { const item = record(value); return item?.handle !== ticket.handle || item.added > ticket.added; });
      writeCartRecords(kept);
    } else if (selectionRemoved) writeCartRecords(raw);
    refresh();
    return { status: removed ? 'removed' : 'retained', handle: ticket.handle, removed, selectionRemoved };
  }
  function record(item) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
    const handle = handleValid(item.handle) ? item.handle : handleFromURL(item.url);
    if (!handle) return null;
    const saved = { handle, title: typeof item.title === 'string' ? item.title.slice(0, 700) : handle.replace(/-/g, ' '), image: imageURL(item.image), price: typeof item.price === 'string' ? item.price.slice(0, 100) : '', url: productURL(handle), added: Number.isFinite(item.added) ? item.added : 0 };
    const observation = priceObservation(item.priceObservation);
    if (observation) saved.priceObservation = observation;
    saved.note = savedNote(item.note);
    saved.priority = savedPriority(item.priority);
    saved.listId = storedListId(item.listId);
    if (Array.isArray(item.cartSelections)) saved.cartSelections = cartSelections(item);
    return saved;
  }
  function getItems() {
    let list;
    try { list = JSON.parse(localStorage.getItem(KEY) || '[]'); storageUnavailable = false; }
    catch (error) { if (!(error instanceof SyntaxError)) storageUnavailable = true; return []; }
    if (!Array.isArray(list)) return [];
    const unique = new Map();
    list.forEach(raw => {
      const item = record(raw);
      if (!item) return;
      unique.set(item.handle, item);
      if (Number.isSafeInteger(item.added) && item.added < Number.MAX_SAFE_INTEGER) lastAdded = Math.max(lastAdded, item.added);
    });
    return [...unique.values()];
  }
  function announce(message) {
    const notice = document.querySelector('[data-sth-wishlist-notice]');
    if (!notice) return;
    clearTimeout(noticeTimer); notice.textContent = message; notice.hidden = false;
    noticeTimer = setTimeout(() => { notice.hidden = true; }, 5500);
  }
  function write(list, message, clear = false) {
    try {
      if (clear) localStorage.removeItem(KEY);
      else localStorage.setItem(KEY, JSON.stringify(list));
      if (localStorage.getItem(KEY) !== (clear ? null : JSON.stringify(list))) throw new Error('Readback did not match.');
      storageUnavailable = false;
    } catch (_) {
      announce('We could not update your saved items. Browser storage may be unavailable or full.');
      return false;
    }
    refresh(); announce(message); return true;
  }
  function canEditCartTransfer(handle = null) {
    let allowed = true;
    try {
      const marker = JSON.parse(localStorage.getItem('sth_saved_cart_active_v1') || 'null');
      const age = Date.now() - marker?.at;
      const active = marker && typeof marker.token === 'string' && marker.token && (marker.handle === '*' || handleValid(marker.handle)) && Number.isFinite(marker.at) && age >= -5000 && age <= 120000;
      if (active && (handle === null || marker.handle === '*' || marker.handle === handle)) allowed = false;
    } catch (_) { allowed = false; }
    if (!allowed) announce('This item is being moved. Please wait.');
    return allowed;
  }
  function toggle(raw) {
    const item = record(raw);
    if (!item) return false;
    let list;
    try { list = readCartRecords(); } catch (_) { announce('Saving is unavailable in this browser.'); return false; }
    const index = cartRecordIndex(list, item.handle);
    let added;
    if (index >= 0) {
      if (!canEditCartTransfer(item.handle)) return false;
      list = list.filter(value => record(value)?.handle !== item.handle);
    }
    else {
      lastAdded = Math.max(Date.now(), lastAdded + 1, ...list.map(saved => Number.isSafeInteger(saved?.added) && saved.added < Number.MAX_SAFE_INTEGER ? saved.added + 1 : 0));
      added = { ...item, listId: DEFAULT_LIST, added: lastAdded }; delete added.priceObservation; list.push(added);
    }
    invalidate(item.handle);
    const written = write(list, index >= 0 ? 'Removed from saved items.' : 'Saved for later.');
    if (written && added) fetchProduct(added.handle).then(value => observeSaved(added, value));
    return written;
  }
  function remove(handle) {
    if (!canEditCartTransfer(handle)) return false;
    let list;
    try { list = readCartRecords(); } catch (_) { announce('Saved items could not be updated in this browser.'); return false; }
    invalidate(handle);
    return write(list.filter(item => record(item)?.handle !== handle), 'Removed from saved items.');
  }
  function clear() {
    if (!canEditCartTransfer()) return false;
    new Set([...cache.keys(), ...pending.keys(), ...incarnations.keys()]).forEach(invalidate);
    return write([], 'Your saved items have been cleared.', true);
  }
  function buttonItem(button) {
    return record({ handle: button.dataset.sthSaveHandle, title: button.dataset.sthSaveTitle, image: button.dataset.sthSaveImage, url: button.dataset.sthSaveUrl });
  }
  function syncButtons(items) {
    const handles = new Set(items.map(item => item.handle));
    document.querySelectorAll('[data-sth-save-handle]').forEach(button => {
      if (!buttonItem(button)) return;
      const saved = handles.has(button.dataset.sthSaveHandle);
      button.hidden = false;
      button.classList.toggle('is-saved', saved);
      button.setAttribute('aria-pressed', String(saved));
      const title = button.dataset.sthSaveTitle || 'this item';
      button.setAttribute('aria-label', saved ? 'Remove ' + title + ' from saved items' : 'Save ' + title + ' for later');
      button.title = saved ? 'Remove from saved items' : 'Save item';
      const label = button.querySelector('[data-sth-save-label]');
      if (label) label.textContent = saved ? 'Saved' : 'Save item';
    });
    document.querySelectorAll('[data-sth-wishlist-link]').forEach(link => {
      link.setAttribute('aria-label', 'Saved items' + (items.length ? ', ' + items.length + (items.length === 1 ? ' item' : ' items') : ''));
    });
    document.querySelectorAll('[data-sth-wishlist-count]').forEach(count => {
      count.textContent = String(items.length); count.hidden = items.length === 0;
    });
  }
  function attachCards(scope = document) {
    const cards = [...scope.querySelectorAll(CARD_SELECTOR)];
    if (scope.matches?.(CARD_SELECTOR)) cards.unshift(scope);
    cards.forEach(card => {
      if (card.querySelector('[data-sth-save-handle]') || card.closest('[data-sth-wishlist-page]')) return;
      const link = [...card.querySelectorAll('a[href]')].find(anchor => handleFromURL(anchor.href));
      const handle = link && handleFromURL(link.href);
      const mount = card.matches('.sth-discovery-card') ? card : card.querySelector('figure');
      if (!handle || !mount) return;
      const title = card.querySelector('h3,h2,[class*="title"]')?.textContent.trim() || link.title || card.querySelector('img')?.alt || handle.replace(/-/g, ' ');
      const button = element('button', 'sth-save-button sth-save-card');
      button.type = 'button'; button.dataset.sthSaveHandle = handle; button.dataset.sthSaveTitle = title;
      button.dataset.sthSaveUrl = productURL(handle); button.dataset.sthSaveImage = card.querySelector('img')?.currentSrc || card.querySelector('img')?.src || '';
      button.innerHTML = HEART;
      const label = element('span', '', 'Save item'); label.dataset.sthSaveLabel = ''; button.append(label);
      mount.classList.add('sth-save-mount'); mount.append(button);
    });
  }
  function money(value) {
    try { return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value / 100); }
    catch (_) { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value / 100); }
  }
  function currentProduct(raw, handle, observedAt, responseCurrency) {
    if (!raw || raw.handle !== handle || !validId(raw.id) || typeof raw.title !== 'string') throw new Error('Product data is unavailable.');
    const min = raw.price_min ?? raw.price, max = raw.price_max ?? min;
    if (!Number.isFinite(min) || !Number.isFinite(max) || min < 0 || max < min) throw new Error('Product price is unavailable.');
    const observation = priceObservation({ version: 1, productId: String(raw.id), currency: responseCurrency, min, max, variantIds: Array.isArray(raw.variants) ? raw.variants.map(variant => variant?.id) : [], observedAt });
    return { handle, id: String(raw.id), title: raw.title, image: imageURL(raw.featured_image || raw.images?.[0]), url: productURL(handle), min, max, available: raw.available === true, observation };
  }
  function invalidate(handle) {
    requestVersions.set(handle, (requestVersions.get(handle) || 0) + 1);
    cache.delete(handle); pending.delete(handle);
  }
  function observeSaved(item, value) {
    const observation = value?.product?.observation;
    if (!observation || value.version !== (requestVersions.get(item.handle) || 0) || storageUnavailable) return item;
    let list;
    try { list = JSON.parse(localStorage.getItem(KEY) || '[]'); }
    catch (error) { if (!(error instanceof SyntaxError)) storageUnavailable = true; return item; }
    if (!Array.isArray(list)) return item;
    let index = -1;
    for (let candidate = list.length - 1; candidate >= 0; candidate--) {
      if (record(list[candidate])?.handle === item.handle) { index = candidate; break; }
    }
    if (index < 0) return null;
    const current = record(list[index]);
    if (current.added !== item.added) return null;
    const first = current.priceObservation;
    if (comparablePrices(first, observation) || (first && first.observedAt > observation.observedAt)) return current;
    list[index] = { ...list[index], priceObservation: observation };
    try { localStorage.setItem(KEY, JSON.stringify(list)); }
    catch (_) { storageUnavailable = true; return current; }
    return { ...current, priceObservation: observation };
  }
  function pump() {
    while (active < 4 && queue.length) {
      const job = queue.shift(); active += 1;
      job().finally(() => { active -= 1; pump(); });
    }
  }
  function fetchProduct(handle) {
    if (cache.has(handle)) return Promise.resolve(cache.get(handle));
    if (pending.has(handle)) return pending.get(handle);
    const version = requestVersions.get(handle) || 0;
    const result = new Promise(resolve => {
      queue.push(async () => {
        if (version !== (requestVersions.get(handle) || 0)) { resolve({ error: 'outdated', version }); return; }
        const controller = new AbortController(); const timer = setTimeout(() => controller.abort(), 8000);
        const responseCurrency = currency;
        let value;
        try {
          const response = await fetch(productURL(handle) + '.js', { signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'application/json' } });
          if (!response.ok) { const error = new Error('Product request failed.'); error.status = response.status; throw error; }
          const raw = await response.json(), observedAt = Date.now();
          value = { product: currentProduct(raw, handle, observedAt, responseCurrency), version };
        } catch (error) { value = { error: error.status === 404 ? 'missing' : 'unavailable' }; }
        finally { clearTimeout(timer); if (pending.get(handle) === result) pending.delete(handle); }
        if (version === (requestVersions.get(handle) || 0)) cache.set(handle, value);
        resolve(value);
      });
    });
    pending.set(handle, result); pump(); return result;
  }
  function checkedDate(timestamp) {
    try { return new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'short', day: 'numeric' }).format(timestamp); }
    catch (_) { return new Date(timestamp).toISOString().slice(0, 10); }
  }
  function priceDetails(item, fresh) {
    const current = fresh.observation, first = item.priceObservation;
    if (!current) return null;
    if (!comparablePrices(first, current) || current.observedAt < first.observedAt || (first.min === current.min && first.max === current.max)) return element('p', 'sth-wishlist-price-checked', 'Price checked ' + checkedDate(current.observedAt));
    const change = element('div', 'sth-wishlist-price-change');
    const difference = current.min - first.min;
    const exact = current.min === current.max && first.min === first.max;
    change.dataset.sthPriceChange = difference < 0 ? 'lower' : difference > 0 ? 'higher' : 'range';
    const delta = difference ? (exact ? 'Price ' : 'Starting price ') + money(Math.abs(difference)) + (difference < 0 ? ' lower' : ' higher') : 'Price range changed since your first check';
    const baseline = first.min === first.max ? money(first.min) : money(first.min) + '–' + money(first.max);
    change.append(element('p', 'sth-wishlist-price-delta', delta), element('p', 'sth-wishlist-price-baseline', 'First checked ' + checkedDate(first.observedAt) + ': ' + baseline));
    return change;
  }
  function savedCard(item, value) {
    const card = element('li', 'sth-wishlist-card'); card.dataset.savedHandle = item.handle;
    const fresh = value?.product, product = fresh || item;
    if (product.image) {
      const anchor = element('a', 'sth-wishlist-image'); anchor.href = productURL(item.handle); anchor.tabIndex = -1; anchor.setAttribute('aria-hidden', 'true');
      const image = element('img'); image.src = product.image; image.alt = ''; image.loading = 'lazy'; image.width = 360; image.height = 360; anchor.append(image); card.append(anchor);
    }
    const title = element('h2', 'sth-wishlist-title');
    const link = element('a', '', product.title); link.href = productURL(item.handle); title.append(link); card.append(title);
    const effective = effectiveListId(item);
    if (activeList === ALL_LISTS && effective !== DEFAULT_LIST) card.append(element('p', 'sth-wishlist-list-name', listLabel(effective)));
    if (item.priority === 'high' || item.priority === 'low') {
      const priority = element('p', 'sth-wishlist-priority', item.priority === 'high' ? 'High priority' : 'Low priority');
      priority.dataset.sthSavedPriorityValue = item.priority; card.append(priority);
    }
    if (item.note?.trim()) card.append(element('p', 'sth-wishlist-item-note', item.note));
    if (fresh) {
      card.append(element('p', 'sth-wishlist-price', fresh.min === fresh.max ? money(fresh.min) : money(fresh.min) + '–' + money(fresh.max)));
      const details = priceDetails(item, fresh); if (details) card.append(details);
      if (!fresh.available) card.append(element('p', 'sth-wishlist-availability', 'Currently unavailable'));
    } else if (value?.error) {
      card.append(element('p', 'sth-wishlist-error', value.error === 'missing' ? 'This product is no longer available.' : 'Current details could not be loaded.'));
      if (value.error !== 'missing') { const retry = element('button', 'sth-wishlist-retry', 'Try again'); retry.type = 'button'; retry.dataset.sthWishlistRetry = item.handle; card.append(retry); }
    } else card.append(element('p', 'sth-wishlist-loading-card', 'Checking current details…'));
    const moves = element('div', 'sth-wishlist-cart-moves'); moves.dataset.sthCartMoves = ''; card.append(moves);
    if (value?.error !== 'missing') { const view = element('a', 'sth-wishlist-view', 'View product'); view.href = productURL(item.handle); card.append(view); }
    const edit = element('button', 'sth-wishlist-edit', 'Edit saved item'); edit.type = 'button'; edit.dataset.sthSavedEdit = item.handle;
    edit.setAttribute('aria-label', 'Edit saved item: ' + product.title); card.append(edit);
    const removeButton = element('button', 'sth-wishlist-remove', 'Remove'); removeButton.type = 'button'; removeButton.dataset.sthWishlistRemove = item.handle; removeButton.setAttribute('aria-label', 'Remove ' + product.title + ' from saved items'); card.append(removeButton);
    window.STHSavedCart?.decorateCard?.(card, item);
    return card;
  }
  function setupPDPListChooser() {
    const dialog = document.querySelector('[data-sth-pdp-list-dialog]');
    const form = dialog?.querySelector('[data-sth-pdp-list-form]');
    const title = dialog?.querySelector('[data-sth-pdp-list-product]');
    const description = dialog?.querySelector('[data-sth-pdp-list-description]');
    const select = dialog?.querySelector('[data-sth-pdp-list-select]');
    const field = dialog?.querySelector('[data-sth-pdp-list-name-field]');
    const name = dialog?.querySelector('[data-sth-pdp-list-name]');
    const status = dialog?.querySelector('[data-sth-pdp-list-status]');
    const cancel = dialog?.querySelector('[data-sth-pdp-list-cancel]');
    const submit = dialog?.querySelector('[data-sth-pdp-list-submit]');
    if (!dialog || !form || !title || !description || !select || !field || !name || !status || !cancel || !submit || typeof dialog.showModal !== 'function' || typeof dialog.close !== 'function') return () => {};
    let editing = null, opener = null;
    const productFromButton = button => {
      if (!button.matches('button[data-sth-pdp-list-open]') || !button.closest('.sth-save-product') || button.closest(CARD_SELECTOR)) return null;
      const handle = button.dataset.sthListProductHandle;
      if (!handleValid(handle) || handleFromURL(button.dataset.sthListProductUrl) !== handle) return null;
      return record({ handle, title: button.dataset.sthListProductTitle, url: button.dataset.sthListProductUrl, image: button.dataset.sthListProductImage });
    };
    const reveal = () => document.querySelectorAll('[data-sth-pdp-list-open]').forEach(button => { if (productFromButton(button)) button.hidden = false; });
    const creationMode = focus => {
      const creating = select.value === 'new';
      field.hidden = !creating; name.disabled = !creating;
      if (creating && focus) name.focus();
    };
    const options = (state, selected) => {
      listOptions(select, state.lists, selected);
      const option = element('option', '', 'Create a new list'); option.value = 'new'; select.append(option);
      select.value = selected; creationMode(false);
    };
    const readProducts = () => {
      let raw;
      try { raw = JSON.parse(localStorage.getItem(KEY) || '[]'); storageUnavailable = false; }
      catch (_) { storageUnavailable = true; throw new Error('Your saved items could not be read. Please try again.'); }
      if (!Array.isArray(raw)) throw new Error('Your saved items could not be read. Please try again.');
      return raw;
    };
    const findCurrent = raw => {
      for (let index = raw.length - 1; index >= 0; index--) {
        const item = record(raw[index]);
        if (item?.handle === editing.product.handle) return { index, item };
      }
      return { index: -1, item: null };
    };
    const validateCurrent = (raw, state) => {
      const current = findCurrent(raw);
      if (!editing.present && current.item) throw new Error('This product was saved while this window was open. Close it and choose the list again.');
      if (editing.present && (!current.item || current.item.added !== editing.added)) throw new Error('This product was removed or saved again. Close this window and choose the list again.');
      if (current.item && effectiveListId(current.item, state.lists) !== editing.listId) throw new Error('This product’s list changed while this window was open. Close it and choose the list again.');
      return current;
    };
    const reportFailure = message => {
      const partial = editing?.created;
      status.textContent = partial ? 'List “' + partial.name + '” was created, but the product was not ' + (editing.present ? 'moved. ' : 'saved. ') + message : message;
    };
    document.addEventListener('click', event => {
      const button = event.target.closest('[data-sth-pdp-list-open]');
      if (!button || dialog.open) return;
      const product = productFromButton(button);
      if (!product) return;
      event.preventDefault(); opener = button; editing = null;
      title.textContent = product.title; description.textContent = ''; status.textContent = ''; name.value = '';
      select.disabled = false; submit.disabled = false;
      field.hidden = true; name.disabled = true;
      try {
        const state = readListState();
        if (!state.raw) throw new Error('Your lists could not be read. Close this window and try again.');
        const raw = readProducts();
        editing = { product, present: false, added: null, listId: DEFAULT_LIST, created: null };
        const current = findCurrent(raw).item;
        editing.present = Boolean(current); editing.added = current?.added ?? null;
        editing.listId = current ? effectiveListId(current, state.lists) : DEFAULT_LIST;
        options(state, editing.listId);
        description.textContent = current ? 'This product is already saved. Choose a list to move it while keeping its note, priority and price checks.' : 'Save this product to one of your lists in this browser.';
      } catch (error) {
        editing = null; select.replaceChildren(); select.disabled = true; submit.disabled = true;
        status.textContent = error.message;
      }
      dialog.showModal(); (editing ? select : cancel).focus();
    });
    select.addEventListener('change', () => { status.textContent = ''; creationMode(true); });
    cancel.addEventListener('click', () => dialog.close());
    dialog.addEventListener('cancel', event => { event.preventDefault(); dialog.close(); });
    dialog.addEventListener('close', () => {
      const handle = editing?.product.handle || opener?.dataset.sthListProductHandle;
      const target = opener?.isConnected ? opener : [...document.querySelectorAll('[data-sth-pdp-list-open]')].find(button => button.dataset.sthListProductHandle === handle && productFromButton(button));
      editing = null; opener = null; target?.focus({ preventScroll: true });
    });
    form.addEventListener('submit', event => {
      event.preventDefault();
      if (!editing || !dialog.open) return;
      try {
        let state = readListState();
        if (!state.raw) throw new Error('Your lists could not be read. Please try again.');
        let raw = readProducts(); validateCurrent(raw, state);
        let destination = select.value;
        if (destination === 'new') {
          const proposed = listName(name.value);
          if (!proposed || proposed.length > 60) throw new Error('Enter a list name between 1 and 60 characters.');
          if (reservedListName(proposed) || state.lists.some(list => listNameKey(list.name) === listNameKey(proposed))) throw new Error('That list name is already in use. Choose a different name.');
          if (state.lists.length >= 20) throw new Error('You can create up to 20 lists. Rename or delete a list before creating another.');
          const id = newListId(state);
          state = readListState();
          if (!state.raw) throw new Error('Your lists could not be read. Please try again.');
          if (state.lists.some(list => listNameKey(list.name) === listNameKey(proposed))) throw new Error('That list name is already in use. Choose a different name.');
          if (state.lists.length >= 20) throw new Error('You can create up to 20 lists. Rename or delete a list before creating another.');
          const created = { id, name: proposed, created: Date.now() };
          const listRaw = [...state.raw, created];
          try { localStorage.setItem(LIST_KEY, JSON.stringify(listRaw)); listsUnavailable = false; }
          catch (_) { listsUnavailable = true; throw new Error('Your new list could not be saved. Browser storage may be unavailable or full. Please try again.'); }
          editing.created = created; destination = created.id;
          state = { raw: listRaw, lists: [...state.lists, created] };
          options(state, destination); name.value = '';
        }
        state = readListState();
        if (!state.raw) throw new Error('Your lists could not be read. Please try again.');
        if (destination !== DEFAULT_LIST && !state.lists.some(list => list.id === destination)) throw new Error('The selected list is no longer available. Close this window and choose another list.');
        raw = readProducts();
        const current = validateCurrent(raw, state);
        let added = null;
        if (current.item) raw[current.index] = { ...raw[current.index], listId: destination };
        else {
          lastAdded = Math.max(Date.now(), lastAdded + 1, ...raw.map(value => Number.isSafeInteger(value?.added) && value.added < Number.MAX_SAFE_INTEGER ? value.added + 1 : 0));
          added = { ...editing.product, listId: destination, added: lastAdded }; delete added.priceObservation;
          raw.push(added); invalidate(added.handle);
        }
        try { localStorage.setItem(KEY, JSON.stringify(raw)); storageUnavailable = false; }
        catch (_) { storageUnavailable = true; throw new Error('Your saved item could not be updated. Browser storage may be unavailable or full. Please try again.'); }
        const label = listLabel(destination, state.lists);
        refresh();
        if (added) fetchProduct(added.handle).then(value => observeSaved(added, value));
        dialog.close(); announce('Saved to ' + label + '.');
      } catch (error) { reportFailure(error.message); }
    });
    reveal(); return reveal;
  }
  function setupListEditor() {
    const dialog = page?.querySelector('[data-sth-list-editor]');
    const form = dialog?.querySelector('[data-sth-list-form]');
    const title = dialog?.querySelector('[data-sth-list-title]');
    const description = dialog?.querySelector('[data-sth-list-description]');
    const field = dialog?.querySelector('[data-sth-list-name-field]');
    const input = dialog?.querySelector('[data-sth-list-name]');
    const status = dialog?.querySelector('[data-sth-list-status]');
    const submit = dialog?.querySelector('[data-sth-list-submit]');
    const cancel = dialog?.querySelector('[data-sth-list-cancel]');
    if (!dialog || !form || !title || !description || !field || !input || !status || !submit || !cancel) return;
    let editing = null;
    document.addEventListener('click', event => {
      const button = event.target.closest('[data-sth-list-create],[data-sth-list-rename],[data-sth-list-delete]');
      if (!button || !page.contains(button) || dialog.open || page.querySelector('[data-sth-saved-editor]')?.open) return;
      event.preventDefault();
      const state = readListState();
      if (!state.raw) { announce('Your lists could not be read. Please try again.'); return; }
      const mode = button.matches('[data-sth-list-create]') ? 'create' : button.matches('[data-sth-list-rename]') ? 'rename' : 'delete';
      const list = state.lists.find(value => value.id === activeList);
      if (mode !== 'create' && !list) { refresh(); announce('This list is no longer available.'); return; }
      editing = { mode, id: list?.id, name: list?.name, created: list?.created, opener: button, saved: false };
      const deleting = mode === 'delete';
      title.textContent = mode === 'create' ? 'New list' : deleting ? 'Delete list' : 'Rename list';
      description.textContent = deleting ? 'Delete “' + list.name + '”? Products will stay saved in Saved for later. Notes, priorities and price checks will be kept.' : 'Choose a name of up to 60 characters. This list is saved in this browser.';
      field.hidden = deleting; input.disabled = deleting; input.value = mode === 'create' ? '' : list.name;
      submit.textContent = mode === 'create' ? 'Create list' : deleting ? 'Delete list' : 'Save name';
      status.textContent = ''; dialog.showModal(); (deleting ? cancel : input).focus();
    });
    cancel.addEventListener('click', () => dialog.close());
    dialog.addEventListener('cancel', event => { event.preventDefault(); dialog.close(); });
    dialog.addEventListener('close', () => {
      const prior = editing; editing = null;
      const target = prior && !prior.saved && prior.opener.isConnected && !prior.opener.hidden ? prior.opener : page.querySelector('[data-sth-list-select]');
      target?.focus({ preventScroll: true });
    });
    form.addEventListener('submit', event => {
      event.preventDefault();
      if (!editing || !dialog.open) return;
      const state = readListState();
      if (!state.raw) { status.textContent = 'Your lists could not be read. No changes were saved. Please try again.'; return; }
      const current = state.lists.find(list => list.id === editing.id);
      if (editing.mode !== 'create' && (!current || current.name !== editing.name || current.created !== editing.created)) {
        status.textContent = 'This list changed in another tab or was deleted. Close this window and reopen the list before making changes.';
        return;
      }
      const name = listName(input.value);
      if (editing.mode !== 'delete') {
        if (!name || name.length > 60) { status.textContent = 'Enter a list name between 1 and 60 characters.'; return; }
        if (reservedListName(name) || state.lists.some(list => !(editing.mode === 'rename' && list.id === editing.id) && listNameKey(list.name) === listNameKey(name))) {
          status.textContent = 'That list name is already in use. Choose a different name.'; return;
        }
      }
      let raw = [...state.raw], next = activeList, message;
      if (editing.mode === 'create') {
        if (state.lists.length >= 20) { status.textContent = 'You can create up to 20 lists. Rename or delete a list before creating another.'; return; }
        let id;
        try { id = newListId(state); } catch (error) { status.textContent = error.message; return; }
        raw.push({ id, name, created: Date.now() }); next = id; message = 'List created.';
      } else if (editing.mode === 'rename') {
        raw[current.index] = { ...raw[current.index], name }; next = current.id; message = 'List renamed.';
      } else {
        raw = raw.filter(list => typeof list?.id !== 'string' || list.id.toLowerCase() !== current.id);
        next = ALL_LISTS; message = 'List deleted. Its products are still saved in Saved for later.';
      }
      try { localStorage.setItem(LIST_KEY, JSON.stringify(raw)); listsUnavailable = false; }
      catch (_) { listsUnavailable = true; status.textContent = 'We could not save your list changes. Browser storage may be unavailable or full. Please try again.'; return; }
      activeList = next; visible = PAGE_SIZE; editing.saved = true;
      refresh(); dialog.close(); announce(message);
    });
  }
  function setupSavedEditor() {
    const dialog = page?.querySelector('[data-sth-saved-editor]');
    const form = dialog?.querySelector('[data-sth-saved-details-form]');
    const title = dialog?.querySelector('[data-sth-saved-editor-product]');
    const note = dialog?.querySelector('[data-sth-saved-note]');
    const priority = dialog?.querySelector('[data-sth-saved-priority]');
    const destination = dialog?.querySelector('[data-sth-saved-list]');
    const status = dialog?.querySelector('[data-sth-saved-editor-status]');
    if (!dialog || !form || !title || !note || !priority || !status) return;
    let editing = null;
    document.addEventListener('click', event => {
      const button = event.target.closest('[data-sth-saved-edit]');
      if (!button || !page.contains(button) || dialog.open || page.querySelector('[data-sth-list-editor]')?.open) return;
      event.preventDefault();
      const item = getItems().find(saved => saved.handle === button.dataset.sthSavedEdit);
      if (storageUnavailable || !item) { announce('This saved item could not be opened. Please try again.'); return; }
      const state = readListState();
      if (!state.raw) { announce('Your lists could not be read. Please try again.'); return; }
      editing = { handle: item.handle, added: item.added, note: item.note, priority: item.priority, listId: effectiveListId(item, state.lists) };
      title.textContent = cache.get(item.handle)?.product?.title || item.title;
      note.value = item.note; priority.value = item.priority; status.textContent = '';
      listOptions(destination, state.lists, editing.listId);
      dialog.showModal(); note.focus();
    });
    dialog.querySelector('[data-sth-saved-editor-cancel]')?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('cancel', event => { event.preventDefault(); dialog.close(); });
    dialog.addEventListener('close', () => {
      const handle = editing?.handle; editing = null;
      const target = [...page.querySelectorAll('[data-sth-saved-edit]')].find(button => button.dataset.sthSavedEdit === handle) || page.querySelector('[data-sth-list-select]') || page.querySelector('[data-sth-saved-edit]') || page.querySelector('[data-sth-wishlist-empty] a');
      target?.focus({ preventScroll: true });
    });
    form.addEventListener('submit', event => {
      event.preventDefault();
      if (!editing || !dialog.open) return;
      let list;
      try { list = JSON.parse(localStorage.getItem(KEY) || '[]'); storageUnavailable = false; }
      catch (_) { storageUnavailable = true; status.textContent = 'We could not read your saved items. Your changes have not been saved. Please try again.'; return; }
      if (!Array.isArray(list)) { status.textContent = 'Your saved items could not be read. Your changes have not been saved.'; return; }
      let index = -1;
      for (let candidate = list.length - 1; candidate >= 0; candidate--) {
        if (record(list[candidate])?.handle === editing.handle) { index = candidate; break; }
      }
      const current = index < 0 ? null : record(list[index]);
      if (!current || current.added !== editing.added) {
        status.textContent = 'This item was removed or saved again while you were editing. Close this window and reopen the item to make changes.';
        return;
      }
      const state = readListState();
      if (!state.raw) { status.textContent = 'Your lists could not be read. Your changes have not been saved. Please try again.'; return; }
      if (current.note !== editing.note || current.priority !== editing.priority || effectiveListId(current, state.lists) !== editing.listId) {
        status.textContent = 'The note, priority or list changed in another tab. Your changes have not been saved. Close this window and reopen the item to see the latest details.';
        return;
      }
      const listId = destination ? destination.value : editing.listId;
      if (listId !== DEFAULT_LIST && !state.lists.some(list => list.id === listId)) {
        status.textContent = 'The selected list is no longer available. Close this window and choose another list.'; return;
      }
      list[index] = { ...list[index], note: savedNote(note.value), priority: savedPriority(priority.value), listId };
      try { localStorage.setItem(KEY, JSON.stringify(list)); storageUnavailable = false; }
      catch (_) { storageUnavailable = true; status.textContent = 'We could not save your changes. Browser storage may be unavailable or full. Please try again.'; return; }
      refresh(); dialog.close(); announce('Saved item updated.');
    });
  }
  function renderPage(items) {
    if (!page?.isConnected) return;
    loadLists(); renderListTools(items);
    const version = ++generation;
    const list = page.querySelector('[data-sth-wishlist-list]');
    const empty = page.querySelector('[data-sth-wishlist-empty]');
    const count = page.querySelector('[data-sth-wishlist-count-text]');
    const loading = page.querySelector('[data-sth-wishlist-loading]');
    const more = page.querySelector('[data-sth-wishlist-more]');
    const clearButton = page.querySelector('[data-sth-wishlist-clear]');
    const refreshButton = page.querySelector('[data-sth-wishlist-refresh]');
    const status = page.querySelector('[data-sth-wishlist-page-status]');
    const sorted = filteredItems(items);
    const shown = sorted.slice(0, visible);
    list.replaceChildren(...shown.map(item => {
      const value = cache.get(item.handle);
      return savedCard(value ? observeSaved(item, value) || item : item, value);
    }));
    list.hidden = !sorted.length; empty.hidden = sorted.length > 0 || storageUnavailable || listsUnavailable;
    const emptyTitle = empty.querySelector('[data-sth-list-empty-title]'), emptyDescription = empty.querySelector('[data-sth-list-empty-description]');
    if (!emptyCopy) emptyCopy = { title: emptyTitle?.textContent || '', description: emptyDescription?.textContent || '' };
    if (emptyTitle) emptyTitle.textContent = activeList === ALL_LISTS ? emptyCopy.title : 'This list is empty';
    if (emptyDescription) emptyDescription.textContent = activeList === ALL_LISTS ? emptyCopy.description : 'Move saved items into this list with Edit saved item.';
    const showAll = empty.querySelector('[data-sth-list-show-all]');
    if (showAll) showAll.hidden = activeList === ALL_LISTS;
    count.textContent = activeList === ALL_LISTS ? (items.length ? items.length + ' saved item' + (items.length === 1 ? '' : 's') : '') : sorted.length + ' item' + (sorted.length === 1 ? '' : 's') + ' in ' + listLabel(activeList);
    clearButton.hidden = !items.length || activeList !== ALL_LISTS; more.hidden = sorted.length <= visible;
    if (refreshButton) refreshButton.hidden = !sorted.length || storageUnavailable;
    more.textContent = 'Show more (' + Math.max(0, sorted.length - visible) + ')';
    loading.hidden = true;
    status.textContent = storageUnavailable ? 'Saved items are unavailable because browser storage is blocked.' : listsUnavailable ? 'Your lists could not be read. Your saved products have not been changed. Please try again.' : sorted.length ? 'Showing ' + shown.length + ' of ' + sorted.length + ' saved items' + (activeList === ALL_LISTS ? '.' : ' in ' + listLabel(activeList) + '.') : '';
    shown.forEach(item => {
      if (cache.has(item.handle)) return;
      fetchProduct(item.handle).then(value => {
        const latest = observeSaved(item, value);
        if (!latest) return;
        if (version !== generation || !page.isConnected) return;
        const existing = [...list.children].find(card => card.dataset.savedHandle === item.handle);
        if (existing) {
          const focused = existing.contains(document.activeElement);
          const editingFocused = focused && document.activeElement.matches('[data-sth-saved-edit]');
          const cartMoveFocused = focused && document.activeElement.matches('[data-sth-cart-move]') ? { handle: document.activeElement.dataset.sthCartMove, selection: document.activeElement.dataset.sthCartSelection || '' } : null;
          const replacement = savedCard(latest, value); existing.replaceWith(replacement);
          if (focused) {
            const movedButton = cartMoveFocused && [...replacement.querySelectorAll('[data-sth-cart-move]')].find(button => button.dataset.sthCartMove === cartMoveFocused.handle && (button.dataset.sthCartSelection || '') === cartMoveFocused.selection);
            (movedButton || replacement.querySelector(editingFocused ? '[data-sth-saved-edit]' : '[data-sth-wishlist-retry],.sth-wishlist-view,[data-sth-wishlist-remove]'))?.focus({ preventScroll: true });
          }
        }
      });
    });
  }
  function refresh() {
    const items = getItems(), next = new Map(items.map(item => [item.handle, item.added]));
    new Set([...cache.keys(), ...pending.keys()]).forEach(handle => { if (incarnations.get(handle) !== next.get(handle)) invalidate(handle); });
    incarnations = next;
    syncButtons(items); renderPage(items);
  }
  function init() {
    const config = document.querySelector('[data-sth-wishlist-config]');
    if (!config) return;
    try {
      const route = new URL(config.dataset.rootUrl || '/', location.origin);
      if (route.origin === location.origin) rootPath = route.pathname.replace(/\/?$/, '/');
    } catch (_) {}
    currency = config.dataset.currency || 'USD'; locale = config.dataset.locale || document.documentElement.lang || 'en';
    page = document.querySelector('[data-sth-wishlist-page]');
    if (page) activeList = new URL(location.href).searchParams.get('list') || ALL_LISTS;
    window.STHSavedItems = Object.freeze({ getItems, toggle, remove, clear, refresh, ensureCartSelection, finishCartSelectionRemoval, beginCartMove, finishCartMove });
    attachCards(); refresh(); setupSavedEditor(); setupListEditor();
    const revealPDPLists = setupPDPListChooser();
    document.addEventListener('change', event => {
      if (event.target.matches('[data-sth-list-select]') && page?.contains(event.target)) chooseList(event.target.value);
    });
    document.addEventListener('click', event => {
      const button = event.target.closest('button');
      if (!button) return;
      if (button.matches('[data-sth-save-handle]')) { event.preventDefault(); event.stopPropagation(); toggle(buttonItem(button)); }
      else if (button.matches('[data-sth-list-show-all]')) { chooseList(ALL_LISTS); page?.querySelector('[data-sth-list-select]')?.focus(); }
      else if (button.matches('[data-sth-wishlist-remove]')) {
        const list = page?.querySelector('[data-sth-wishlist-list]');
        const index = [...(list?.children || [])].findIndex(card => card.dataset.savedHandle === button.dataset.sthWishlistRemove);
        if (remove(button.dataset.sthWishlistRemove)) {
          const targets = [...(list?.querySelectorAll('[data-sth-wishlist-remove]') || [])];
          (targets[Math.min(Math.max(index, 0), targets.length - 1)] || page?.querySelector('[data-sth-list-select]') || page?.querySelector('[data-sth-wishlist-empty] a'))?.focus({ preventScroll: true });
        }
      } else if (button.matches('[data-sth-wishlist-clear]')) {
        if (activeList === ALL_LISTS && clear()) page?.querySelector('[data-sth-wishlist-empty] a')?.focus({ preventScroll: true });
      } else if (button.matches('[data-sth-wishlist-more]')) { visible += PAGE_SIZE; renderPage(getItems()); }
      else if (button.matches('[data-sth-wishlist-retry]')) { invalidate(button.dataset.sthWishlistRetry); renderPage(getItems()); }
      else if (button.matches('[data-sth-wishlist-refresh]')) {
        const items = getItems();
        if (storageUnavailable) return;
        loadLists(); filteredItems(items).slice(0, visible).forEach(item => invalidate(item.handle));
        renderPage(items);
      }
    });
    window.addEventListener('storage', event => { if (event.key === KEY || event.key === LIST_KEY || event.key === null) refresh(); });
    window.addEventListener('pageshow', event => { if (event.persisted) { new Set([...cache.keys(), ...pending.keys()]).forEach(invalidate); refresh(); } });
    const observer = new MutationObserver(mutations => {
      const relevant = mutations.some(mutation => [...mutation.addedNodes].some(node => node.nodeType === 1 && (node.matches(CARD_SELECTOR + ',[data-sth-save-handle],[data-sth-wishlist-link],[data-sth-pdp-list-open]') || node.querySelector(CARD_SELECTOR + ',[data-sth-save-handle],[data-sth-wishlist-link],[data-sth-pdp-list-open]'))));
      if (!relevant || scanTimer) return;
      scanTimer = setTimeout(() => { scanTimer = null; attachCards(); syncButtons(getItems()); revealPDPLists(); }, 50);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
